import { Component } from 'preact';
import { motion, AnimatePresence } from 'framer-motion';
import AppHeader from '../AppHeader';
import { BackButton } from '../ui/navButtons';
import AmbientParticles from '../AmbientParticles';
import {
  BEVELED_BTN, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD, GOLD, VIGNETTE,
  KnotOrnament,
} from '../../lib/medievalTheme';
import { UI } from '../../utils/arena/uiSounds';
import { listDraftEvents, DRAFT_STATUS } from '../../utils/arena/draftApi';
import { getVisibleModes } from '../../utils/arena/gameModes';
import ModeTile from './ModeTile';
import { t } from '../../localization/t';

export default class PlayScreen extends Component {
  constructor(props) {
    super(props);
    this.state = {
      liveData: {},
      hoveredModeKey: null,
    };
  }

  handleModeHoverChange = (modeKey, isHovered) => {
    this.setState((s) => {
      if (isHovered) return { hoveredModeKey: modeKey };
      // Only clear when the currently-hovered tile reports leaving.
      // Protects against mouse-enter of a new tile landing after the
      // previous tile's mouse-leave, which would otherwise blank the
      // description between hops.
      if (s.hoveredModeKey === modeKey) return { hoveredModeKey: null };
      return null;
    });
  };

  componentDidMount() {
    this.fetchLiveData();
  }

  componentWillUnmount() {
    this._unmounted = true;
  }

  async fetchLiveData() {
    const { currentSeason } = this.props;
    const liveData = { season: currentSeason || null };
    try {
      const events = await listDraftEvents({ status: DRAFT_STATUS.OPEN });
      if (!this._unmounted) {
        liveData.openDraftEvents = Array.isArray(events) ? events.length : 0;
        this.setState({ liveData });
      }
    } catch {
      if (!this._unmounted) {
        this.setState({ liveData });
      }
    }
  }

  handleModeClick = (mode) => {
    mode.onEnter?.(this.buildDeps());
  };

  buildDeps() {
    const { onOpenRanked, onOpenSandbox, onOpenCasual, onOpenDraft, onOpenFriendMatch } = this.props;
    return { onOpenRanked, onOpenSandbox, onOpenCasual, onOpenDraft, onOpenFriendMatch };
  }

  render() {
    const {
      profile, onBack, onToggleMailbox, mailboxUnreadCount, mailboxDropdown,
      onToggleFriends, friendListData, draftQueueDropdown, sorceryCards,
    } = this.props;
    const { liveData } = this.state;

    const visible = getVisibleModes(profile);
    const hoveredMode = this.state.hoveredModeKey
      ? visible.find((e) => e.mode.key === this.state.hoveredModeKey)?.mode
      : null;

    return (
      <div
        className="fixed inset-0 z-50 flex flex-col"
        style={{
          background: `url('/play-bg.webp') center/cover no-repeat, #08080a`,
                  }}
      >
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.45) 40%, rgba(0,0,0,0.5) 70%, rgba(0,0,0,0.85) 100%)' }}
        />
        <div className="absolute inset-0 pointer-events-none" style={{ background: VIGNETTE }} />
        <AmbientParticles preset="hub" />

        <AppHeader
          profile={profile}
          onToggleMailbox={onToggleMailbox}
          mailboxUnreadCount={mailboxUnreadCount}
          mailboxDropdown={mailboxDropdown}
          draftQueueDropdown={draftQueueDropdown}
          onToggleFriends={onToggleFriends}
          friendListData={friendListData}
          zoom={1}
        >
          <BackButton title={t('Back')} onClick={onBack} />
        </AppHeader>

        <div className="relative z-10 flex-1 min-h-0 overflow-y-auto">
          <div className="mx-auto max-w-[92.5cqw] w-full px-10 pt-12 pb-10 flex flex-col gap-8">
            {/* Decorative flourish. Drops the redundant "GAME MODES"
                title. The tiles ARE the answer on an action screen,
                a giant header reads as a settings menu. Keeping the
                flanking knots + fading hairlines preserves the
                cross-screen ornamental vocabulary we established in
                Settings without adding a redundant label. */}
            <header className="flex items-center justify-center gap-5" aria-hidden="true">
              <KnotOrnament side="left" size={28} />
              <div
                className="h-px w-40"
                style={{ background: `linear-gradient(90deg, transparent 0%, ${GOLD} 0.4) 50%, transparent 100%)` }}
              />
              <KnotOrnament side="right" size={28} />
            </header>

            {/* Staggered entrance. Tiles ease in with a 60ms
                cascade so the eye naturally reads the grid left→right
                top→bottom on first paint. AAA screens almost always
                have this kind of "reveal" motion; without it the
                layout lands like a settings page. */}
            <div
              className="grid gap-4"
              style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}
            >
              {visible.map(({ mode, availability }, idx) => (
                <motion.div
                  key={mode.key}
                  initial={{ opacity: 0, y: 14, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{
                    duration: 0.42,
                    delay: idx * 0.06,
                    ease: [0.22, 0.8, 0.36, 1],
                  }}
                >
                  <ModeTile
                    mode={mode}
                    available={availability.available}
                    reason={availability.reason}
                    badges={mode.getBadges(profile, liveData) || []}
                    sorceryCards={sorceryCards}
                    onHoverChange={(h) => this.handleModeHoverChange(mode.key, h)}
                    onClick={() => this.handleModeClick(mode)}
                  />
                </motion.div>
              ))}
            </div>

            {/* Hover description panel. Always reserves space so the
                grid position is stable; when nothing is hovered we
                show a soft italic prompt that reads as intentional
                breathing room instead of a missing component. */}
            <div
              className="relative flex items-center justify-center"
              style={{ minHeight: '6.75cqw' }}
              aria-live="polite"
            >
              {/* Default empty-state prompt. Fades out the moment a
                  tile is hovered and the real description fades in. */}
              <AnimatePresence>
                {!hoveredMode ? (
                  <motion.div
                    key="empty-state"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="flex items-center gap-4 pointer-events-none"
                  >
                    <div
                      className="h-px w-10"
                      style={{ background: `linear-gradient(90deg, transparent 0%, ${GOLD} 0.35) 100%)` }}
                    />
                    <span
                      className="text-[0.75cqw] italic"
                      style={{ color: 'rgba(166,160,155,0.42)' }}
                    >
                      {t('Hover a mode to learn more')}
                    </span>
                    <div
                      className="h-px w-10"
                      style={{ background: `linear-gradient(90deg, ${GOLD} 0.35) 0%, transparent 100%)` }}
                    />
                  </motion.div>
                ) : null}
              </AnimatePresence>
              <AnimatePresence mode="wait">
                {hoveredMode ? (
                  <motion.div
                    key={hoveredMode.key}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                    className="vh-mode-desc-panel absolute inset-x-0 top-0 mx-auto"
                    style={{ maxWidth: '51.25cqw' }}
                  >
                    {/* Warm gold accent rail on the left. Reads as
                        the panel being "keyed" to a live hovered
                        tile, without borrowing the tile's whole
                        colour. */}
                    <div
                      className="absolute top-4 bottom-4 left-0"
                      style={{
                        width: 3,
                        borderRadius: 'var(--radius-xs)',
                        background: `linear-gradient(180deg, ${ACCENT_GOLD} 0%, rgba(212,168,67,0.1) 100%)`,
                        boxShadow: '0 0 10px rgba(232,195,110,0.45)',
                      }}
                    />
                    <div className="flex items-baseline gap-4 mb-2.5">
                      <h3
                        className="arena-heading text-xl font-bold tracking-wide"
                        style={{
                          color: TEXT_PRIMARY,
                          textShadow: '0 -1px 0 rgba(0,0,0,0.7), 0 1px 0 rgba(255,220,140,0.15), 0 2px 5px rgba(0,0,0,0.7)',
                        }}
                      >
                        {t(hoveredMode.title)}
                      </h3>
                      {hoveredMode.tagline ? (
                        <span
                          className="text-[0.6875cqw] font-semibold"
                          style={{ color: `${GOLD} 0.65)`, textShadow: `0 0 10px ${GOLD} 0.25)` }}
                        >
                          {t(hoveredMode.tagline)}
                        </span>
                      ) : null}
                    </div>
                    <p
                      className="text-[0.8125cqw] leading-relaxed italic"
                      style={{ color: 'rgba(166,160,155,0.78)', maxWidth: '42.5cqw' }}
                    >
                      {t(hoveredMode.description)}
                    </p>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
